/**
 * The request-access dialog: a modal card (a bottom sheet under 600px) over
 * the stage, with the form, its error summary, the submitting, failure and
 * success states. Mounted only while open, so its entrance animations run
 * from the first frame and its focus rules apply only while it exists.
 *
 * Accessibility, as the reference has it: `role="dialog"`, `aria-modal`,
 * labelled by the title and described by the description; the backdrop
 * closes only when a pointer goes down *and* up on it; every input names
 * its error element through `aria-describedby` and carries `aria-invalid`;
 * the error summary is a focusable `role="alert"` whose links move focus to
 * the field; the failure notice is an alert; the confirmation is a polite
 * live region; email and link inputs are LTR in both languages.
 */

import {
  useEffect,
  useRef,
  type SyntheticEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import type { PublicEarlyAccessCopy } from './copy.js';
import type { EarlyAccessForm } from './useEarlyAccessForm.js';
import { useDialogFocus } from './useDialogFocus.js';
import type { FieldName } from './validation.js';

export const DIALOG_IDS = {
  title: 'cs-ac-title',
  description: 'cs-ac-desc',
  summary: 'cs-ac-summary',
  submit: 'cs-ac-submit',
  doneClose: 'cs-ac-done-close',
  field: (field: FieldName) => `cs-f-${field}`,
  error: (field: FieldName) => `cs-e-${field}`,
} as const;

const GRID_DIAGONALS = [0, 1, 2, 3, 1, 2, 3, 4, 2, 3, 4, 5, 3, 4, 5, 6] as const;

interface FieldSpec {
  readonly name: FieldName;
  readonly type: 'text' | 'email';
  readonly autoComplete: string;
  readonly inputMode?: 'email' | 'url';
  readonly ltr: boolean;
  readonly optional: boolean;
}

const FIELDS: readonly FieldSpec[] = [
  { name: 'name', type: 'text', autoComplete: 'name', ltr: false, optional: false },
  {
    name: 'email',
    type: 'email',
    autoComplete: 'email',
    inputMode: 'email',
    ltr: true,
    optional: false,
  },
  {
    name: 'brand',
    type: 'text',
    autoComplete: 'organization',
    ltr: false,
    optional: false,
  },
  {
    name: 'link',
    type: 'text',
    autoComplete: 'url',
    inputMode: 'url',
    ltr: true,
    optional: true,
  },
];

function fieldLabel(copy: PublicEarlyAccessCopy, field: FieldName): string {
  return copy[field];
}

function fieldError(copy: PublicEarlyAccessCopy, field: FieldName): string {
  switch (field) {
    case 'name':
      return copy.eName;
    case 'email':
      return copy.eEmail;
    case 'brand':
      return copy.eBrand;
    case 'link':
      return copy.eLink;
  }
}

export function EarlyAccessDialog({
  copy,
  form,
  onClose,
}: {
  readonly copy: PublicEarlyAccessCopy;
  readonly form: EarlyAccessForm;
  readonly onClose: () => void;
}): ReactNode {
  const card = useRef<HTMLDivElement | null>(null);
  const scrimDown = useRef(false);
  const success = form.phase === 'success';

  useDialogFocus({
    card,
    initialFocus: () =>
      document.getElementById(
        success ? DIALOG_IDS.doneClose : DIALOG_IDS.field('name'),
      ),
    onEscape: onClose,
  });

  // Focus owed by the last state change: the summary after an invalid
  // attempt, the submit button after a failure, a field from a summary link,
  // the Close button once the confirmation replaces the form.
  const request = form.focusRequest;
  useEffect(() => {
    if (request === null) return;
    const target =
      request.kind === 'summary'
        ? DIALOG_IDS.summary
        : request.kind === 'submit'
          ? DIALOG_IDS.submit
          : DIALOG_IDS.field(request.field);
    document.getElementById(target)?.focus();
  }, [request]);
  useEffect(() => {
    if (!success) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(DIALOG_IDS.doneClose)?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [success]);

  const onBackdropPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    scrimDown.current = event.target === event.currentTarget;
  };
  const onBackdropPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const onBackdrop = event.target === event.currentTarget;
    if (scrimDown.current && onBackdrop) onClose();
    scrimDown.current = false;
  };

  const onSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.submit();
  };

  const submitting = form.phase === 'submitting';

  return (
    <div
      className="cs-access"
      data-open="true"
      data-phase={form.phase}
      onPointerDown={onBackdropPointerDown}
      onPointerUp={onBackdropPointerUp}
    >
      <div
        ref={card}
        className="cs-access-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={DIALOG_IDS.title}
        aria-describedby={success ? undefined : DIALOG_IDS.description}
      >
        <button
          type="button"
          className="cs-access-close"
          aria-label={copy.close}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onClick={onClose}
        >
          ×
        </button>

        {success ? (
          <div className="cs-done" aria-live="polite" aria-atomic="true">
            <span className="cs-pxgrid" aria-hidden="true">
              {GRID_DIAGONALS.map((diagonal, index) => (
                <i key={index} style={{ '--n': diagonal } as never} />
              ))}
            </span>
            <h2 id={DIALOG_IDS.title}>{copy.doneTitle}</h2>
            <p>{copy.doneText}</p>
            <button
              type="button"
              className="cs-done-close"
              id={DIALOG_IDS.doneClose}
              onClick={onClose}
            >
              {copy.close}
            </button>
          </div>
        ) : (
          <div className="cs-form-view">
            <p className="cs-access-eyebrow">
              <i aria-hidden="true" />
              {copy.acEyebrow}
            </p>
            <h2 id={DIALOG_IDS.title}>{copy.acTitle}</h2>
            <p className="cs-access-desc" id={DIALOG_IDS.description}>
              {copy.acDesc}
            </p>
            <form noValidate onSubmit={onSubmit}>
              {form.summaryShown ? (
                <div
                  className="cs-summary"
                  id={DIALOG_IDS.summary}
                  role="alert"
                  aria-atomic="true"
                  tabIndex={-1}
                >
                  <strong>{copy.summaryTitle(form.invalid.length)}</strong>
                  <ul>
                    {form.invalid.map((field) => (
                      <li key={field}>
                        <a
                          href={`#${DIALOG_IDS.field(field)}`}
                          onClick={(event) => {
                            event.preventDefault();
                            form.focusField(field);
                          }}
                        >
                          {fieldError(copy, field)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="cs-fields">
                {FIELDS.map((spec) => {
                  const shown = form.shownErrors.includes(spec.name);
                  return (
                    <div className="cs-field" key={spec.name}>
                      <label className="cs-lab" htmlFor={DIALOG_IDS.field(spec.name)}>
                        <span>{fieldLabel(copy, spec.name)}</span>
                        {spec.optional ? <small>{copy.optional}</small> : null}
                      </label>
                      <input
                        className="cs-inp"
                        id={DIALOG_IDS.field(spec.name)}
                        name={spec.name}
                        type={spec.type}
                        autoComplete={spec.autoComplete}
                        {...(spec.inputMode ? { inputMode: spec.inputMode } : {})}
                        {...(spec.ltr
                          ? { dir: 'ltr' as const, spellCheck: false }
                          : {})}
                        {...(spec.name === 'name' ? { autoCapitalize: 'words' } : {})}
                        required={!spec.optional}
                        aria-required={!spec.optional}
                        aria-invalid={shown}
                        aria-describedby={DIALOG_IDS.error(spec.name)}
                        disabled={submitting}
                        value={form.values[spec.name]}
                        onChange={(event) => {
                          form.input(spec.name, event.target.value);
                        }}
                        onBlur={() => {
                          form.blur(spec.name);
                        }}
                      />
                      <p
                        className="cs-err"
                        id={DIALOG_IDS.error(spec.name)}
                        data-show={shown}
                      >
                        {shown ? fieldError(copy, spec.name) : null}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="cs-access-foot">
                {form.phase === 'error' ? (
                  <p className="cs-fail" role="alert" aria-atomic="true">
                    <i aria-hidden="true" />
                    <span>{copy.fail}</span>
                  </p>
                ) : null}
                <button
                  type="submit"
                  className="cs-submit"
                  id={DIALOG_IDS.submit}
                  data-ready={form.ready}
                  aria-disabled={!form.ready}
                  aria-busy={submitting}
                >
                  <span className="cs-spinner" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span>{submitting ? copy.submitting : copy.submit}</span>
                </button>
                <p className="cs-privacy">{copy.privacy}</p>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
